<script setup lang="ts">
/**
 * Everything still standing between the customer and the order.
 *
 * This exists so the CTA and its reasons cannot disagree — `canPlaceOrder` is
 * derived from the same list — and it is what the CTA's `aria-describedby`
 * points at, so a keyboard or screen-reader user who reaches a button they
 * cannot press is told why rather than left guessing.
 *
 * It is a polite live region: requirements are met and unmet as the customer
 * works down the page, and a checklist that only ever redraws is a change
 * announced to nobody. `aria-atomic` is deliberately OFF — re-reading the whole
 * list every time one item clears is noise; the changed rows are the news.
 */
defineProps<{
  blockers: { key: string; label: string }[]
}>()
</script>

<template>
  <div
    role="status"
    aria-live="polite"
    class="empty:hidden"
  >
    <div
      v-if="blockers.length > 0"
      class="space-y-1.5 rounded-md border border-default bg-elevated/40 p-3"
    >
      <p class="text-xs font-medium">
        {{ $t('billing.page.checkout.blockers.title') }}
      </p>
      <ul class="space-y-1">
        <li
          v-for="b in blockers"
          :key="b.key"
          class="flex items-center gap-1.5 text-xs text-muted"
        >
          <UIcon
            name="i-lucide-circle-alert"
            class="size-3.5 shrink-0 text-warning"
            aria-hidden="true"
          />
          <span>{{ b.label }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>
